import { useCallback } from 'react';

const openDatabase = async (dbName, storeName) => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(dbName);

    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(storeName)) {
        db.createObjectStore(storeName);
      }
    };

    request.onsuccess = (event) => {
      const db = event.target.result;

      // Check if store exists, if not, trigger upgrade
      if (!db.objectStoreNames.contains(storeName)) {
        db.close();
        // Increment version to trigger onupgradeneeded
        const versionRequest = indexedDB.open(dbName, db.version + 1);

        versionRequest.onupgradeneeded = (e) => {
          const newDb = e.target.result;
          newDb.createObjectStore(storeName);
        };

        versionRequest.onsuccess = (e) => {
          resolve(e.target.result);
        };

        versionRequest.onerror = reject;
      } else {
        resolve(db);
      }
    };

    request.onerror = reject;
  });
};

export const useIndexedDB = (dbName, storeName) => {
  const save = useCallback(async (key, data) => {
    const db = await openDatabase(dbName, storeName);
    const tx = db.transaction(storeName, 'readwrite');
    const store = tx.objectStore(storeName);
    store.put(data, key);
    return tx.complete;
  }, [dbName, storeName]);

  const load = useCallback(async (key) => {
    const db = await openDatabase(dbName, storeName);
    const tx = db.transaction(storeName, 'readonly');
    const store = tx.objectStore(storeName);
    return new Promise((resolve, reject) => {
      const request = store.get(key);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }, [dbName, storeName]);

  const remove = useCallback(async (key) => {
    const db = await openDatabase(dbName, storeName);
    const tx = db.transaction(storeName, 'readwrite');
    const store = tx.objectStore(storeName);
    store.delete(key);
    return tx.complete;
  }, [dbName, storeName]);

  const clear = useCallback(async () => {
    const db = await openDatabase(dbName, storeName);
    const tx = db.transaction(storeName, 'readwrite');
    const store = tx.objectStore(storeName);
    store.clear();
    return tx.complete;
  }, [dbName, storeName]);

  return { save, load, remove, clear };
};



export const saveToIndexedDB = async (dbName, storeName, key, data) => {
  const db = await openDatabase(dbName, storeName);
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, 'readwrite');
    const store = tx.objectStore(storeName);
    const request = store.put(data, key);
    request.onsuccess = () => resolve(true);
    request.onerror = () => reject(request.error);
  });
};

export const loadFromIndexedDB = async (dbName, storeName, key) => {
  const db = await openDatabase(dbName, storeName);
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, 'readonly');
    const store = tx.objectStore(storeName);
    const request = store.get(key);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
};

export const deleteFromIndexedDB = async (dbName, storeName, key) => {
  const db = await openDatabase(dbName, storeName);
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, 'readwrite');
    const store = tx.objectStore(storeName);
    const request = store.delete(key);
    request.onsuccess = () => resolve(true);
    request.onerror = () => reject(request.error);
  });
};

export const clearIndexedDB = async (dbName, storeName) => {
  const db = await openDatabase(dbName, storeName);
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, 'readwrite');
    const store = tx.objectStore(storeName);
    const request = store.clear();
    request.onsuccess = () => resolve(true);
    request.onerror = () => reject(request.error);
  });
};


export const deleteStoreFromIndexedDB = async (dbName, storeName) => {
  return new Promise((resolve, reject) => {
    // First open the database to check current version
    const openRequest = indexedDB.open(dbName);

    openRequest.onsuccess = (event) => {
      const db = event.target.result;
      const currentVersion = db.version;
      db.close();

      // Now reopen with incremented version to trigger onupgradeneeded
      const deleteRequest = indexedDB.open(dbName, currentVersion + 1);

      deleteRequest.onupgradeneeded = (event) => {
        const db = event.target.result;
        if (db.objectStoreNames.contains(storeName)) {
          db.deleteObjectStore(storeName);
        }
      };

      deleteRequest.onsuccess = () => {
        deleteRequest.result.close();
        resolve(true);
      };

      deleteRequest.onerror = (event) => {
        reject(event.target.error);
      };
    };

    openRequest.onerror = (event) => {
      reject(event.target.error);
    };
  });
};
